// BuySellCyprus search scanner - fetches and parses search result pages
// Fetches HTML via Cloudflare Worker proxy and extracts property listing cards

import { fetchHtmlViaProxy, sanitizeText, extractBuySellId } from './buysellService';

/**
 * Normalize a BuySellCyprus property URL to point to the details page.
 * Strips trailing /gallery, /request-information, .html, query params, etc.
 * @param {string} url - Raw property URL
 * @returns {string} - Clean details page URL
 */
const normalizePropertyUrl = (url) => {
  let clean = url.split('?')[0].split('#')[0]; // Strip query/hash
  clean = clean.replace(/\/(gallery|request-information)\/?$/i, ''); // Strip subpages
  clean = clean.replace(/\.html$/i, ''); // Strip .html extension
  clean = clean.replace(/\/+$/, ''); // Strip trailing slashes
  return clean;
};

/**
 * Walk up the DOM from an element to find a reasonable "card" container.
 * Looks for an ancestor that contains price-like text or is large enough
 * to be a listing card.
 * @param {Element} el - Starting element
 * @param {number} maxLevels - Maximum levels to walk up
 * @returns {Element|null} - Card container or null
 */
const findCardContainer = (el, maxLevels = 6) => {
  // First try known CSS class patterns
  const knownCard = el.closest(
    'div[class*="card"], div[class*="listing"], div[class*="property"], div[class*="result"], div[class*="item"], div[class*="col"], article, li'
  );
  if (knownCard) {
    const text = knownCard.textContent || '';
    // Make sure it has meaningful content (not just a tiny wrapper)
    if (text.length > 20) return knownCard;
  }

  // Walk up the DOM looking for a container with price/content
  let current = el.parentElement;
  for (let level = 0; level < maxLevels && current; level++) {
    const text = current.textContent || '';
    // A good card container has enough text and ideally contains a price
    if (text.length > 40 && text.match(/€|EUR|\d{3,}/)) {
      return current;
    }
    // Or it's a div/article/section/li with reasonable content
    if (
      text.length > 80 &&
      ['DIV', 'ARTICLE', 'SECTION', 'LI'].includes(current.tagName)
    ) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

/**
 * Extract the best title from a property link and its surrounding context.
 * Tries multiple strategies in order of reliability.
 * @param {Element} link - The <a> element
 * @param {Element|null} card - The card container
 * @param {Array<Element>} allLinksForId - All links pointing to the same property
 * @returns {string}
 */
const extractTitle = (link, card, allLinksForId) => {
  // Strategy 1: Link's title attribute
  const titleAttr = link.getAttribute('title');
  if (titleAttr && titleAttr.length > 5) return titleAttr;

  // Strategy 2: Check all links for this property ID for title attributes or text
  for (const l of allLinksForId) {
    const attr = l.getAttribute('title');
    if (attr && attr.length > 5) return attr;

    const text = l.textContent?.trim();
    if (text && text.length > 5 && text.length < 200 && !text.match(/^\s*$/)) {
      // Make sure it's not just a number or "View"
      if (!text.match(/^[\d,.\s€£$]+$/) && !text.match(/^(View|See|More|Details)$/i)) {
        return text;
      }
    }
  }

  // Strategy 3: img alt text inside any link for this property
  for (const l of allLinksForId) {
    const img = l.querySelector('img');
    if (img) {
      const alt = img.getAttribute('alt');
      if (alt && alt.length > 5 && !alt.match(/logo|icon|placeholder/i)) return alt;
    }
  }

  // Strategy 4: Heading elements (h2-h5) inside the card
  if (card) {
    for (const tag of ['h2', 'h3', 'h4', 'h5']) {
      const heading = card.querySelector(tag);
      if (heading) {
        const text = heading.textContent?.trim();
        if (text && text.length > 3 && text.length < 200) return text;
      }
    }
  }

  // Strategy 5: Link's own text content (last resort, might be empty for image links)
  const linkText = link.textContent?.trim();
  if (linkText && linkText.length > 3 && linkText.length < 200) return linkText;

  return '';
};

/**
 * Extract price from a card container or link context.
 * @param {Element|null} card - Card container
 * @param {Element} link - The <a> element
 * @returns {number} - Price in euros, or 0 if not found
 */
const extractPrice = (card, link) => {
  // Strategy 1: Look in the card container
  if (card) {
    const cardText = card.textContent || '';
    const priceMatch = cardText.match(/€\s?([\d,.]+)/);
    if (priceMatch) {
      return parseInt(priceMatch[1].replace(/[^0-9]/g, ''), 10) || 0;
    }
    // Also try "EUR" format
    const eurMatch = cardText.match(/EUR\s?([\d,.]+)/i);
    if (eurMatch) {
      return parseInt(eurMatch[1].replace(/[^0-9]/g, ''), 10) || 0;
    }
  }

  // Strategy 2: Walk up from the link specifically looking for price
  let el = link.parentElement;
  for (let i = 0; i < 8 && el; i++) {
    const text = el.textContent || '';
    const priceMatch = text.match(/€\s?([\d,.]+)/);
    if (priceMatch) {
      return parseInt(priceMatch[1].replace(/[^0-9]/g, ''), 10) || 0;
    }
    el = el.parentElement;
  }

  return 0;
};

/**
 * Scan a BuySellCyprus search results page and return normalized results.
 * Uses a two-pass approach:
 *   Pass 1: Find all property links and group by property ID
 *   Pass 2: For each unique property, extract title and price from the best context
 *
 * @param {string} searchUrl - BuySellCyprus search page URL
 *   e.g. https://www.buysellcyprus.com/properties-for-sale/location-paphos/type-house
 * @returns {Promise<Array<{id: string, title: string, price: number|string, url: string}>>}
 */
export const scanBuySellSearch = async (searchUrl) => {
  console.log(`[BuySell Search] Fetching: ${searchUrl}`);
  const html = await fetchHtmlViaProxy(searchUrl);

  // Sanity check: proxy returns raw HTML; parsing happens here
  if (!html || typeof html !== 'string') {
    throw new Error('Proxy returned invalid response');
  }
  if (!/<\s*!?\s*doctype|<\s*html/i.test(html)) {
    throw new Error(
      'Proxy did not return HTML. The property-proxy worker returns raw HTML for parsing — if you see JSON or an error page, check proxy configuration.'
    );
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove scripts, styles to avoid noise (keep nav/footer for now as links might be there)
  const junkSelectors = ['script', 'style', 'noscript', 'iframe'];
  for (const sel of junkSelectors) {
    doc.querySelectorAll(sel).forEach((el) => el.remove());
  }

  // Pass 1: Collect all property links grouped by property ID
  const linksByPropertyId = new Map(); // propertyId -> { links: [], firstHref: '' }
  const allLinks = doc.querySelectorAll('a[href]');

  for (const link of allLinks) {
    const href = link.getAttribute('href') || '';

    // Match individual property page links
    const isPropertyLink =
      /\/property\/\d/i.test(href) ||
      /\/property-for-(?:sale|rent)\/.+\d{5,}/i.test(href) ||
      /\/property-to-rent\/.+\d{5,}/i.test(href);

    if (!isPropertyLink) continue;

    const fullUrl = href.startsWith('http')
      ? href
      : `https://www.buysellcyprus.com${href}`;

    const propertyId = extractBuySellId(fullUrl);
    if (!propertyId) continue;

    const cleanUrl = normalizePropertyUrl(fullUrl);

    if (!linksByPropertyId.has(propertyId)) {
      linksByPropertyId.set(propertyId, { links: [], cleanUrl });
    }
    linksByPropertyId.get(propertyId).links.push(link);
  }

  // Pass 2: Extract title and price for each unique property
  const items = [];

  for (const [propertyId, { links, cleanUrl }] of linksByPropertyId) {
    let bestTitle = '';
    let bestPrice = 0;

    // Try each link for this property, pick the best data
    for (const link of links) {
      const card = findCardContainer(link);

      // Extract title (try all links for this property for context)
      if (!bestTitle) {
        bestTitle = extractTitle(link, card, links);
      }

      // Extract price
      if (!bestPrice) {
        bestPrice = extractPrice(card, link);
      }

      // If we have both, stop searching
      if (bestTitle && bestPrice) break;
    }

    items.push({
      id: propertyId,
      title: sanitizeText(bestTitle, 200),
      price: bestPrice,
      url: cleanUrl,
    });
  }

  console.log(
    `[BuySell Search] Found ${items.length} properties` +
    ` (${items.filter((i) => i.title).length} with titles, ${items.filter((i) => i.price > 0).length} with prices)`
  );
  return items;
};
